/**
 * CurrencyWidget component.
 *
 * A toggle between USD and EUR, click anywhere to flip, matching the
 * sidebar's currency toggle. When first added with no value, it defaults
 * to whichever currency is currently selected in the sidebar.
 *
 * @module components/Widgets/CurrencyWidget/CurrencyWidget
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Button } from 'semantic-ui-react';

import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

const CurrencyWidget = (props) => {
  const { id, value, onChange, isDisabled } = props;

  const sidebarCurrency = useSelector((state) => state.currency);

  // If no value is set yet (e.g. a brand new order), default it to
  // whichever currency is currently selected in the sidebar.
  useEffect(() => {
    if (!value) {
      onChange(id, sidebarCurrency === 'EUR' ? 'EUR' : 'USD');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => onChange(id, value === 'USD' ? 'EUR' : 'USD');

  return (
    <FormFieldWrapper {...props} className="text">
      <div
        className="currency-toggle"
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label="Toggle currency"
        onClick={() => !isDisabled && toggle()}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <Button className={value === 'USD' ? 'primary' : ''} as="span">
          USD
        </Button>
        <Button className={value === 'EUR' ? 'primary' : ''} as="span">
          EUR
        </Button>
      </div>
    </FormFieldWrapper>
  );
};

CurrencyWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
};

CurrencyWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  isDisabled: false,
};

export default CurrencyWidget;
